import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@reflex/ui/components/ui/button';
import { Alert, AlertDescription } from '@reflex/ui/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { PageTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { TOP_PROGRESS } from '@/components/layout/top-progress-store';
import { orgSetupApi } from '@/lib/org-setup-api';
import { queryKeys, orgScope } from '@/lib/query-keys';
import {
  SandboxAccountCard,
  type SandboxAccountPhase,
} from '@/components/setup/SandboxAccountCard';
import { SecretInputDialog } from '@/components/forms/SecretInputDialog';
import { usePlatformUrl } from '@/hooks/config/useConfig';
import { dialogDocsUrl } from './dialog-docs-url';
import { useSandboxHealth } from '@/hooks/org/useSandboxHealth';
import type { SecretStatusItem } from '@/lib/types';
import {
  interpretRunloopProvisioningError,
  type RunloopProvisioningOutcome,
} from '../runloopProvisioning';
import { docs } from '../onboardingDocs';

/**
 * The wizard's only credential stage. Every agent Reflex runs lives in a
 * Runloop sandbox, so an org without a working `RUNLOOP_API_KEY` cannot start
 * anything — which is why this is the one thing onboarding blocks on.
 *
 * The key is never asked for: `POST .../runloop-provider/provision` mints an
 * account server-side, stores its `RUNLOOP_API_KEY` as a **per-org** secret,
 * and pins the account id. The manual `/sandbox-provider` PUT survives only as
 * this stage's post-failure escape hatch (a server with no inprovision
 * credential, an account Runloop refused, a bound key that stopped working),
 * because it writes the same per-org slot `RunloopRegistry` and the devbox
 * env-var refs read (the registry refuses to fall back to the host's
 * unprefixed slot).
 *
 * Model provider keys are deliberately not collected here: they are not needed
 * to finish setup, and every provider stays editable from Model Providers
 * afterwards.
 *
 * We also avoid the generic admin routes (`api.setSecret` /
 * `api.getSecretsStatus`) for the sandbox key: those short-circuit to the
 * unprefixed host slot whenever the caller is a super-admin or single-tenant
 * (see `secret.routes.ts` → `orgScope()`), which is the typical wizard caller
 * right after phase 1.
 */
export function RunloopAccountStage({ orgId, onNext }: { orgId: string; onNext: () => void }) {
  const queryClient = useQueryClient();
  // This deployment's console, for the manual-key hatch below. `null` while
  // `GET /api/config` is in flight; `dialogDocsUrl` falls back.
  const platformUrl = usePlatformUrl();
  const [editingSecret, setEditingSecret] = useState<SecretStatusItem | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // How the last automatic-provisioning attempt ended. `null` while it is
  // still running or once it succeeded. An `in-progress` outcome is not a
  // failure: another tab, another replica, or this wizard before a reload
  // holds the lease, so we keep the "setting up" copy and poll for its key.
  const [provisionOutcome, setProvisionOutcome] = useState<RunloopProvisioningOutcome | null>(null);

  const statusQueryKey = useMemo(() => ['org-setup', orgId, 'secrets-status'] as const, [orgId]);
  const sandboxQueryKey = useMemo(() => ['org-setup', orgId, 'sandbox-provider'] as const, [orgId]);
  const sandboxQuery = useQuery({
    queryKey: sandboxQueryKey,
    queryFn: () => orgSetupApi.getSandboxProvider(orgId),
    // Only while another provision owns the lease: poll until its key lands
    // so this tab resolves without the user reloading. Reading `hasKey` off
    // the query itself (rather than a derived flag) keeps the stop condition
    // in one place and out of an effect.
    refetchInterval: (query) =>
      provisionOutcome?.kind === 'in-progress' && query.state.data?.hasKey !== true ? 3_000 : false,
  });
  const runloopAccount = sandboxQuery.data?.account ?? null;
  const statusQuery = useQuery({
    queryKey: statusQueryKey,
    queryFn: () => orgSetupApi.getSecretsStatus(orgId),
  });

  // Every reader that changes when the org's Runloop binding does: the
  // stage's own two reads, the host-wide admin/secrets caches (an operator
  // re-running the wizard may already have those views mounted), the chat
  // model catalog, and the setup gate's key-health read. Flipping that last
  // one off a cached `invalid` / `missing` / `trial` value is what lets an
  // org that re-entered the wizard only to repair its key bounce straight
  // back to the app.
  async function invalidateSandboxReaders() {
    await queryClient.invalidateQueries({ queryKey: sandboxQueryKey });
    await queryClient.invalidateQueries({ queryKey: statusQueryKey });
    await queryClient.invalidateQueries({ queryKey: queryKeys.secretsStatus });
    await queryClient.invalidateQueries({ queryKey: queryKeys.chatModels });
    await queryClient.invalidateQueries({ queryKey: queryKeys.sandboxHealth(orgScope(orgId)) });
  }

  function onAccountBound(account: { id: string; name: string; tierLabel: string }) {
    toast.success(`Connected to ${account.name}`, {
      description: `Runloop account (${account.id}) · ${account.tierLabel} tier`,
    });
    setSaveError(null);
    setProvisionOutcome(null);
    setEditingSecret(null);
  }

  const setMutation = useMutation({
    mutationFn: (value: string) => orgSetupApi.saveSandboxProvider(orgId, value),
    onSuccess: async (data) => {
      await invalidateSandboxReaders();
      onAccountBound(data.account);
    },
    onError: (err: Error) => {
      setSaveError(err.message);
      toast.error('Failed to save key', { description: err.message });
    },
  });

  const provisionMutation = useMutation({
    // Auto-fired on mount and blocks on Runloop account provisioning for up to
    // minutes; the provisioning card is the affordance, so keep the global top
    // progress bar from trickling the whole time.
    meta: { [TOP_PROGRESS]: false },
    mutationFn: () => orgSetupApi.provisionRunloopAccount(orgId),
    onSuccess: async (data) => {
      await invalidateSandboxReaders();
      onAccountBound(data.account);
    },
    onError: async (err: Error) => {
      // Another provision may have completed between our read and our write
      // (a second tab, or a retry landing after the original finished), so
      // re-read the binding before deciding this is a failure at all: a
      // stored key wins over the outcome recorded here.
      await queryClient.invalidateQueries({ queryKey: sandboxQueryKey });
      const outcome = interpretRunloopProvisioningError(err);
      setProvisionOutcome(outcome);
      if (outcome.kind === 'failed') {
        toast.error('Sandbox setup did not finish', { description: outcome.message });
      }
    },
  });

  // The sandbox provider (RUNLOOP_API_KEY — every agent launch hard-fails
  // without it) is the only core registry secret, and onboarding never
  // renders it: the stage reads the registry solely to hand the fallback
  // dialog the real slot definition (label, docs URL, description).
  // Guard `integrations` with `?.` too: a refetch can transiently resolve to a
  // partial/empty body (e.g. an invalidation racing a teardown), and a bare
  // `.filter` on a present-but-integration-less `data` throws mid-render.
  const runloopSecretItem =
    (statusQuery.data?.integrations ?? [])
      .flatMap((i) => i.secrets ?? [])
      .find((s) => s.key === 'RUNLOOP_API_KEY') ?? null;
  const runloopKeyIsSet = sandboxQuery.data?.hasKey === true || runloopSecretItem?.isSet === true;

  // A bound key that no longer works is the one case a fresh provision can't
  // fix: the endpoint refuses to overwrite an existing credential, and the
  // org is pinned to its account. The setup gate routes such an org back
  // here, so the stage has to name the problem and offer the rotation rather
  // than reporting "ready" and bouncing the user around the gate forever.
  // Same query key the gate reads, so this is normally a cache hit.
  const sandboxHealth = useSandboxHealth(orgId, runloopKeyIsSet);
  const boundKeyIsBroken = runloopKeyIsSet && sandboxHealth.data?.status === 'invalid';

  // A stored key wins over anything an earlier attempt reported, so the
  // failure state is dropped the moment the binding shows up (a concurrent
  // provision finishing, or the manual fallback being used). A binding we
  // cannot even read is its own failure: without it the stage would sit on
  // "setting up" forever, because the request that would set the account up
  // is gated on that read.
  const provisionFailure: { message: string; retryable: boolean } | null = boundKeyIsBroken
    ? {
        message:
          'This organization’s Runloop key no longer works. Add a replacement key for the same Runloop account.',
        retryable: false,
      }
    : runloopKeyIsSet
      ? null
      : provisionOutcome?.kind === 'failed'
        ? provisionOutcome
        : sandboxQuery.isError
          ? {
              message:
                'Reflex could not check this organization’s sandbox setup. Try again in a moment.',
              retryable: true,
            }
          : null;

  // Phase of the server-owned sandbox account. Everything that is neither
  // ready nor failed (first read, mutation in flight, another provision
  // holding the lease) reads as still provisioning.
  const sandboxPhase: SandboxAccountPhase = provisionFailure
    ? 'failed'
    : runloopKeyIsSet
      ? 'ready'
      : 'provisioning';

  // Waiting on a provision this tab does not own. The poll above normally
  // resolves it, but the holder can die mid-flight (a closed tab, a replica
  // restart) and its lease only goes stale after minutes, so the card offers
  // a retry rather than leaving the user with a spinner and no way out.
  const waitingOnConcurrentProvision =
    sandboxPhase === 'provisioning' && provisionOutcome?.kind === 'in-progress';

  // Fire the provision exactly once per mount. Re-entrancy is enforced by
  // the server: a durable lease keyed on `org-setup:<orgId>` makes a repeat
  // call replay the completed operation and return the same account instead
  // of minting a second one. This ref only stops the component from spamming
  // the endpoint across re-renders; a retry after a failure is an explicit
  // user action.
  const provision = provisionMutation.mutate;
  const provisionRequestedRef = useRef(false);
  useEffect(() => {
    if (provisionRequestedRef.current) return;
    // Wait for the first read of the binding: firing before it lands would
    // POST for an org that already has a key.
    if (!sandboxQuery.data) return;
    if (sandboxQuery.data.hasKey) return;
    provisionRequestedRef.current = true;
    provision();
  }, [sandboxQuery.data, provision]);

  function handleRetryProvision() {
    setSaveError(null);
    setProvisionOutcome(null);
    provisionRequestedRef.current = true;
    provision();
  }

  // Escape hatch after automatic setup fails: open the normal secret dialog on
  // the registry's RUNLOOP_API_KEY row. The hand-built fallback keeps it usable
  // when the secrets-status read failed too, so it can't reuse the server's
  // resolved `docsUrl` — and carries none of its own, since `editingSecret` is
  // snapshotted on open. `dialogDocsUrl` resolves the link at render.
  function handleAddKeyManually() {
    setSaveError(null);
    setEditingSecret(
      runloopSecretItem ?? {
        key: 'RUNLOOP_API_KEY',
        label: 'Runloop API Key',
        category: 'integration',
        provider: 'runloop',
        description: 'Per-organization key for devbox provisioning and agent execution.',
        required: true,
        isSet: false,
        source: 'unset',
      },
    );
  }

  // Continue is gated on the sandbox being ready - it is the only thing this
  // stage sets up, and agents can't start without it.
  const canContinue = sandboxPhase === 'ready';
  const continueDisabledReason =
    sandboxPhase === 'provisioning'
      ? 'Sandbox setup is still running. This only takes a moment.'
      : sandboxPhase === 'failed'
        ? 'Finish sandbox setup to continue - agents can’t start without it.'
        : null;

  return (
    <>
      <PageTitle docs={docs('stage-2-runloop-account')}>Connect your Runloop account</PageTitle>
      <PageDescription>
        Reflex runs every agent inside an isolated cloud sandbox, and those sandboxes come from
        Runloop. Each organization needs its own Runloop account, so Reflex is creating one for this
        organization now - there is nothing to sign up for and no key to paste.
      </PageDescription>
      <div className="mt-6 space-y-4">
        {/* Only the sandbox read gates the surface: rendering "creating an
            account" before it lands would state something we don't know yet
            for an org that may already have one. The secrets-status read is
            deliberately not a gate — it only supplies the fallback dialog's
            slot definition, which has a literal default, so a flaky read of
            it must not hide sandbox setup. A failed sandbox read is not a
            gate either: it surfaces on the card as a retryable failure. */}
        {sandboxQuery.isLoading ? (
          <LoadingState message="Loading your sandbox account…" />
        ) : (
          <div data-testid="org-setup-runloop-account">
            <SandboxAccountCard
              phase={sandboxPhase}
              account={runloopAccount}
              message={provisionFailure?.message ?? provisionOutcome?.message ?? null}
              canRetry={waitingOnConcurrentProvision || (provisionFailure?.retryable ?? false)}
              onRetry={handleRetryProvision}
              isRetrying={provisionMutation.isPending || setMutation.isPending}
              onAddKeyManually={handleAddKeyManually}
              docsSlug={docs('runloop-sandbox-key')}
            />
            {/* The dialog shows its own copy of a save failure, so only
                surface it out here once that dialog is closed. */}
            {saveError && !editingSecret ? (
              <Alert variant="destructive" className="mt-2">
                <AlertCircle className="size-4" />
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border/40 pt-4">
          <p className="text-xs text-muted-foreground">
            {continueDisabledReason ??
              'Your sandbox account is ready. Add model provider keys any time from Model Providers.'}
          </p>
          {canContinue ? (
            <Button onClick={onNext} data-testid="org-setup-runloop-continue">
              Continue
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Wrap the disabled button in a span so the tooltip
                    can still pick up pointer events - disabled
                    `<button>`s swallow `mouseenter`/`focus` from Radix
                    triggers, which would silently drop the explanation
                    for the most likely-to-be-stuck users. */}
                <span tabIndex={0} className="inline-flex">
                  <Button disabled aria-disabled data-testid="org-setup-runloop-continue">
                    Continue
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{continueDisabledReason ?? 'Loading your sandbox…'}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {editingSecret && (
        <SecretInputDialog
          key={editingSecret.key}
          open={!!editingSecret}
          onOpenChange={(open) => {
            if (!open) {
              setSaveError(null);
              setEditingSecret(null);
            }
          }}
          secretKey={editingSecret.key}
          label={editingSecret.label}
          description={editingSecret.description}
          docsUrl={dialogDocsUrl(editingSecret, platformUrl)}
          isUpdate={editingSecret.isSet}
          errorMessage={saveError}
          onSubmit={(value) => setMutation.mutate(value)}
          isPending={setMutation.isPending}
        />
      )}
    </>
  );
}
